import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { JobService } from 'src/@core/services/job/job.service';
import { environment } from 'src/environments/environment';
import { GET_FUNNEL_METRICS, PROJECT_API_KEY } from 'src/@core/services/util/api.endpoints';
import { FunnelConfig, FunnelMetricsResponse, FunnelStage, PlacementResponse, toPayload } from './funnel.logic';

const SUGGEST_FUNNEL_PLACEMENT = environment.projectAPI + 'suggestFunnelPlacement';

/** The top-of-page strip reuses a recent answer instead of reading the call history again. */
const CACHE_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class FunnelService {
  private cache: { [key: string]: { at: number; res: FunnelMetricsResponse } } = {};

  constructor(private http: HttpClient, private jobService: JobService) {}

  /** Last `days` days, answered from a five minute cache so showing the strip is not another read per page view. */
  getMetricsCached(entityId: string, projectId: string, days: number): Observable<FunnelMetricsResponse> {
    const key = entityId + '|' + projectId + '|' + days;
    const hit = this.cache[key];
    if (hit && Date.now() - hit.at < CACHE_MS) {
      return of(hit.res);
    }
    const end = new Date();
    const start = new Date(end.getTime() - (days - 1) * 86400000);
    return this.getMetrics(entityId, projectId, this.ymd(start), this.ymd(end)).pipe(
      tap(res => { this.cache[key] = { at: Date.now(), res }; })
    );
  }

  clearCache() {
    this.cache = {};
  }

  private ymd(d: Date): string {
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  /** Dates are YYYY-MM-DD (IST). Leave both out for the last 30 days. */
  getMetrics(
    entityId: string, projectId: string, startDate?: string, endDate?: string, trend?: 'day' | 'week'
  ): Observable<FunnelMetricsResponse> {
    const headers = new HttpHeaders({ 'x-api-key': PROJECT_API_KEY, 'Content-Type': 'application/json' });
    let params = new HttpParams().set('entity_id', entityId).set('project_id', projectId);
    if (startDate && endDate) {
      params = params.set('start_date', startDate).set('end_date', endDate);
    }
    if (trend) {
      params = params.set('trend', trend);
    }
    return this.http.get<FunnelMetricsResponse>(GET_FUNNEL_METRICS, { headers, params });
  }

  /** Saves through the same project update the rest of the app uses. */
  saveFunnel(profile: any, projectId: string, config: FunnelConfig): Observable<any> {
    const form = new FormData();
    form.append('entityId', profile.entity.id);
    form.append('userId', profile.user.id);
    form.append('userName', profile.user.userMeta.name);
    form.append('projectId', projectId);
    form.append('funnelConfig', JSON.stringify(toPayload(config)));
    this.clearCache();
    return this.jobService.updateHoaProject(form);
  }

  /**
   * Asks the server where each outcome belongs. Stages are sent highest priority (P0) first and
   * outcomes by name; the answer maps outcome name to stage id.
   */
  suggestPlacement(profile: any, projectId: string, stages: FunnelStage[], outcomes: string[]): Observable<PlacementResponse> {
    const body = {
      entityId: profile.entity.id,
      projectId,
      stages: stages.slice().reverse().map(s => ({ id: s.id, name: s.name })),
      outcomes: outcomes.map(name => ({ id: name, name }))
    };
    const headers = new HttpHeaders({ 'x-api-key': PROJECT_API_KEY, 'Content-Type': 'application/json' });
    return this.http.post<PlacementResponse>(SUGGEST_FUNNEL_PLACEMENT, body, { headers });
  }
}
